import { type ApiSoftwareProduct, getSoftwareProducts } from 'common/api';
import type { CommercialId } from 'common/services/api.types';
import { setUserProducts } from 'common/services/productAnalytics';
import { isOneOf } from 'common/typeUtils';
import { useAuth } from 'hooks/useAuth';
import { getScheduleOfType } from 'modules/softwareProducts/software.utils';
import useSWRImmutable from 'swr/immutable';

const FleksipakkenCommercialProductId = 316;
export const useSoftwareProducts = () => {
  const token = useAuth().jwtToken;

  const swr = useSWRImmutable(
    token ? ['swr-get-software-products', token] : null,
    async () => {
      const products = await getSoftwareProducts();
      setUserProducts(
        products
          .filter((p) => isOneOf(p.status, ['Active', 'PendingFreeze', 'PendingCancellation']))
          .map((product) => product.name),
      );
      return products.map((p) => ({
        ...p,
        // TODO: map more props here so we dont need to query for it later
        checks: {
          isFrozen: Product_Frozen_Statuses.includes(p.status),
          isPossibleToFreeze: isPossibleToFreeze(p),
          isPossibleToCancel: isPossibleToCancel(p),
          isProductVisibleByStatus: !statusesToHide.includes(p.status),
          isPendingDateActivation: p.status === 'PendingDateActivation',
          isPendingUnknownStartDate: p.status === 'PendingUnknownStartDate',
          isStreamingProduct: p.type === 'Streaming',
          isSubscriptionOrPremium: p.type !== 'None' && p.type !== 'Multiroom',
          isMultiroomSubscription: p.type === 'Multiroom',
          isMainSubscription: isMainSubscription(p),
          isPendingActivationMainSubscription: isPendingActivationMainSubscription(p),
          isActiveMainSubscription: isActiveMainSubscription(p),
        },
      }));
    },
    { errorRetryCount: 1 },
  );

  const hasMainSubscription = swr.data?.some((p) => p.checks.isMainSubscription) ?? false;
  const canChooseAlacarteStreamingProducts =
    swr.data?.some((p) => p.commercialProductId === FleksipakkenCommercialProductId) ?? false;
  return {
    ...swr,
    canChooseAlacarteStreamingProducts,
    hasMainSubscription,
    hasPendingMainSubscription: swr.data?.some((p) => p.checks.isPendingActivationMainSubscription) ?? false,
  };
};

/**
 * Mapping helpers
 */

const isMainSubscription = (product: ApiSoftwareProduct) =>
  product.type === 'Subscription' || product.type === 'Prepaid';

const isPendingActivationMainSubscription = (product: ApiSoftwareProduct) =>
  isMainSubscription(product) &&
  (product.status === 'PendingSystemActivation' ||
    product.status === 'PendingDateActivation' ||
    product.status === 'PendingUnknownStartDate');

const isActiveMainSubscription = (product: ApiSoftwareProduct) =>
  isMainSubscription(product) && product.status === 'Active';

const isPossibleToFreeze = (product: ApiSoftwareProduct) =>
  !notPossibleToFreeze.includes(product.commercialProductId) && possibleToFreeze.includes(product.status);

const isPossibleToCancel = (product: ApiSoftwareProduct) => {
  const productCanBeCancelled = !notPossibleToCancel.includes(product.commercialProductId);
  const hasNoCurrentCancelSchedule = getScheduleOfType(product, 'Cancel') == null;
  const isNotStreamingProduct = product.type !== 'Streaming';
  return productCanBeCancelled && hasNoCurrentCancelSchedule && isNotStreamingProduct;
};

const statusesToHide = ['WriteOff', 'Cancelled'];
const notPossibleToFreeze = [15, 45, 46, 47, 244, 245, 246, 247, 248, 251, 324, 325, 326] as CommercialId[];
const notPossibleToCancel = [15, 45, 46, 47, 244, 245, 246, 247, 248] as CommercialId[];
const Product_Frozen_Statuses = ['Frozen', 'PendingFreeze'];
const possibleToFreeze = ['Active'];

export type SoftwareProduct = NonNullable<ReturnType<typeof useSoftwareProducts>['data']>[number];
